import { useState } from "react";
import { 
  X, ArrowRight, ArrowLeft, CheckCircle, Cpu, Shield, Cloud, 
  Database, Lock, AlertTriangle, TrendingUp, Info
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DigitalReadinessChecker = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    industry: "",
    digital_maturity: "",
    ai_usage: [],
    cybersecurity_measures: [],
    data_management: [],
    compliance_concerns: []
  });

  const industries = [
    { id: "food_manufacturing", name: "Food Manufacturing", icon: "🏭" },
    { id: "agriculture", name: "Agriculture & Farming", icon: "🌾" },
    { id: "hospitality", name: "Hospitality & Food Service", icon: "🍽️" },
    { id: "manufacturing", name: "Other Manufacturing", icon: "⚙️" },
    { id: "services", name: "Professional Services", icon: "💼" },
    { id: "healthcare", name: "Healthcare", icon: "🏥" },
    { id: "government", name: "Government & Public Sector", icon: "🏛️" },
    { id: "finance", name: "Finance & Banking", icon: "🏦" }
  ];

  const digitalMaturityLevels = [
    { id: "basic", name: "Basic", desc: "Primarily paper-based, minimal digital tools" },
    { id: "developing", name: "Developing", desc: "Some digital tools, spreadsheets, basic software" },
    { id: "established", name: "Established", desc: "Integrated systems, cloud services, some automation" },
    { id: "advanced", name: "Advanced", desc: "Fully digital, AI/ML usage, advanced analytics" }
  ];

  const aiUsageOptions = [
    { id: "none", name: "No AI usage currently" },
    { id: "considering", name: "Considering AI implementation" },
    { id: "chatbots", name: "Customer service chatbots" },
    { id: "analytics", name: "Predictive analytics" },
    { id: "automation", name: "Process automation (RPA)" },
    { id: "quality", name: "Quality control / inspection" },
    { id: "supply_chain", name: "Supply chain optimization" },
    { id: "document", name: "Document processing / OCR" }
  ];

  const cybersecurityOptions = [
    { id: "antivirus", name: "Antivirus/Anti-malware", basic: true },
    { id: "firewall", name: "Firewall protection", basic: true },
    { id: "backup", name: "Regular data backups", basic: true },
    { id: "password", name: "Password policies", basic: true },
    { id: "mfa", name: "Multi-factor authentication (MFA)" },
    { id: "encryption", name: "Data encryption (at rest/transit)" },
    { id: "training", name: "Security awareness training" },
    { id: "incident", name: "Incident response plan" },
    { id: "penetration", name: "Regular penetration testing" },
    { id: "soc", name: "Security Operations Center (SOC)" },
    { id: "iso27001", name: "ISO 27001 certified" }
  ];

  const dataManagementOptions = [
    { id: "policy", name: "Data governance policy" },
    { id: "classification", name: "Data classification system" },
    { id: "privacy", name: "Privacy impact assessments" },
    { id: "retention", name: "Data retention procedures" },
    { id: "access", name: "Access control management" },
    { id: "audit", name: "Audit trails / logging" },
    { id: "gdpr", name: "GDPR compliance measures" },
    { id: "consent", name: "Consent management" }
  ];

  const complianceConcerns = [
    { id: "data_privacy", name: "Data Privacy (GDPR, etc.)" },
    { id: "ai_governance", name: "AI Governance & Ethics" },
    { id: "cybersecurity", name: "Cybersecurity Compliance" },
    { id: "industry_specific", name: "Industry-specific regulations" },
    { id: "third_party", name: "Third-party/vendor risk" },
    { id: "cloud", name: "Cloud security & compliance" },
    { id: "iot", name: "IoT device security" },
    { id: "supply_chain", name: "Digital supply chain risk" }
  ];

  const toggleOption = (field, optionId) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(optionId)
        ? prev[field].filter(o => o !== optionId)
        : [...prev[field], optionId]
    }));
  };

  const analyzeReadiness = () => {
    setIsLoading(true);
    
    setTimeout(async () => {
      // Calculate scores
      const cyberScore = Math.min(100, (formData.cybersecurity_measures.length / cybersecurityOptions.length) * 100);
      const dataScore = Math.min(100, (formData.data_management.length / dataManagementOptions.length) * 100);
      const aiReadiness = formData.ai_usage.includes('none') ? 'beginner' :
                         formData.ai_usage.length >= 3 ? 'advanced' : 'developing';
      
      const overallMaturity = formData.digital_maturity === 'advanced' ? 85 :
                             formData.digital_maturity === 'established' ? 65 :
                             formData.digital_maturity === 'developing' ? 40 : 20;
      
      const overallScore = Math.round((cyberScore + dataScore + overallMaturity) / 3);

      // Generate recommendations based on gaps
      const recommendations = [];
      const risks = [];

      // Cybersecurity gaps
      const basicCyber = cybersecurityOptions.filter(o => o.basic).map(o => o.id);
      const missingBasicCyber = basicCyber.filter(id => !formData.cybersecurity_measures.includes(id));
      if (missingBasicCyber.length > 0) {
        risks.push({
          area: "Cybersecurity",
          level: "high",
          description: "Missing basic cybersecurity controls",
          action: "Implement antivirus, firewall, backups, and password policies immediately"
        });
      }

      if (!formData.cybersecurity_measures.includes('mfa')) {
        recommendations.push({
          priority: "high",
          area: "Cybersecurity",
          action: "Implement Multi-Factor Authentication (MFA) for all critical systems",
          benefit: "Reduces account compromise risk by 99%"
        });
      }

      if (!formData.cybersecurity_measures.includes('training')) {
        recommendations.push({
          priority: "high",
          area: "Cybersecurity",
          action: "Establish regular security awareness training for all staff",
          benefit: "Human error causes 95% of breaches"
        });
      }

      // Data management gaps
      if (formData.data_management.length < 3) {
        risks.push({
          area: "Data Management",
          level: "medium",
          description: "Limited data governance controls",
          action: "Develop data classification and access control policies"
        });
      }

      // AI readiness
      if (formData.ai_usage.includes('considering') || formData.ai_usage.includes('none')) {
        recommendations.push({
          priority: "medium",
          area: "AI Readiness",
          action: "Start with low-risk AI use cases like document processing or analytics",
          benefit: "Build AI capabilities incrementally with manageable risk"
        });
      }

      // Compliance concerns
      if (formData.compliance_concerns.includes('ai_governance')) {
        recommendations.push({
          priority: "high",
          area: "AI Governance",
          action: "Develop an AI ethics framework and usage policy",
          benefit: "Prepare for upcoming AI regulations (EU AI Act, etc.)"
        });
      }

      // ISO 27001 recommendation
      if (!formData.cybersecurity_measures.includes('iso27001') && cyberScore >= 50) {
        recommendations.push({
          priority: "medium",
          area: "Information Security",
          action: "Consider ISO 27001 certification to formalize your security program",
          benefit: "Provides framework for continuous improvement and market credibility"
        });
      }

      const analysisResult = {
        id: `digital-${Date.now()}`,
        industry: formData.industry,
        digital_maturity: formData.digital_maturity,
        scores: {
          overall: overallScore,
          cybersecurity: Math.round(cyberScore),
          data_management: Math.round(dataScore),
          ai_readiness: aiReadiness
        },
        maturity_level: overallScore >= 70 ? 'Advanced' : 
                       overallScore >= 50 ? 'Established' :
                       overallScore >= 30 ? 'Developing' : 'Basic',
        risks: risks,
        recommendations: recommendations,
        ai_insights: {
          current_usage: formData.ai_usage.filter(u => u !== 'none' && u !== 'considering'),
          readiness_level: aiReadiness,
          suggested_starting_points: aiReadiness === 'beginner' ? 
            ['Document processing', 'Basic chatbots', 'Reporting automation'] :
            ['Advanced analytics', 'Predictive maintenance', 'Process optimization']
        },
        next_steps: [
          "Address any high-priority cybersecurity gaps immediately",
          "Develop or update your information security policy",
          "Conduct a risk assessment for AI and digital transformation initiatives",
          "Consider ISO 27001 certification for formal security framework",
          "Contact our team for a detailed digital transformation roadmap"
        ]
      };

      // Save to session
      const sessionId = localStorage.getItem("chatbot_session_id");
      if (sessionId) {
        try {
          await axios.post(`${API}/tools/session/save`, {
            session_id: sessionId,
            tool_name: "digital_readiness_checker",
            result: analysisResult
          });
        } catch (e) {
          console.error("Failed to save session:", e);
        }
      }

      setResult(analysisResult);
      setIsLoading(false);
    }, 1500);
  };

  const getScoreColor = (score) => {
    if (score >= 70) return "text-green-600";
    if (score >= 50) return "text-amber-600";
    return "text-red-600";
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">What industry are you in?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                This helps us identify industry-specific digital risks and requirements.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {industries.map((ind) => (
                <button
                  key={ind.id}
                  onClick={() => { setFormData({ ...formData, industry: ind.id }); setStep(2); }}
                  className={`p-4 rounded-xl border-2 text-center transition-all hover:border-brand ${
                    formData.industry === ind.id ? 'border-brand bg-brand/5' : 'border-slate-200'
                  }`}
                >
                  <span className="text-2xl mb-2 block">{ind.icon}</span>
                  <span className="font-medium text-sm">{ind.name}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">What&apos;s your current digital maturity?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                How digitally advanced is your organization?
              </p>
            </div>
            <div className="space-y-3">
              {digitalMaturityLevels.map((level) => (
                <button
                  key={level.id}
                  onClick={() => setFormData({ ...formData, digital_maturity: level.id })}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    formData.digital_maturity === level.id 
                      ? 'border-brand bg-brand/5' 
                      : 'border-slate-200 hover:border-brand/50'
                  }`}
                >
                  <div className="font-medium">{level.name}</div>
                  <div className="text-sm text-muted-foreground">{level.desc}</div>
                </button>
              ))}
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={() => setStep(3)} 
                className="bg-brand hover:bg-brand-dark"
                disabled={!formData.digital_maturity}
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">AI & Automation Usage</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select all AI/automation technologies you use or are considering.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {aiUsageOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => toggleOption('ai_usage', option.id)}
                  className={`p-3 rounded-lg border text-sm text-left transition-all ${
                    formData.ai_usage.includes(option.id) 
                      ? 'border-brand bg-brand text-white' 
                      : 'border-slate-200 hover:border-brand/50'
                  }`}
                >
                  {option.name}
                </button>
              ))}
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={() => setStep(4)} 
                className="bg-brand hover:bg-brand-dark"
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Cybersecurity Measures</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select all cybersecurity controls currently in place.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
              {cybersecurityOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => toggleOption('cybersecurity_measures', option.id)}
                  className={`p-3 rounded-lg border text-sm text-left transition-all ${
                    formData.cybersecurity_measures.includes(option.id) 
                      ? 'border-brand bg-brand text-white' 
                      : 'border-slate-200 hover:border-brand/50'
                  }`}
                >
                  {option.name}
                  {option.basic && <span className="text-xs opacity-70 ml-1">(Basic)</span>}
                </button>
              ))}
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(3)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={() => setStep(5)} 
                className="bg-brand hover:bg-brand-dark"
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Data Management & Compliance</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select data management practices and compliance concerns.
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Data Management Practices</label>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {dataManagementOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => toggleOption('data_management', option.id)}
                    className={`p-2 rounded-lg border text-xs text-left transition-all ${
                      formData.data_management.includes(option.id) 
                        ? 'border-brand bg-brand text-white' 
                        : 'border-slate-200 hover:border-brand/50'
                    }`}
                  >
                    {option.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Key Compliance Concerns</label>
              <div className="grid grid-cols-2 gap-2">
                {complianceConcerns.map((concern) => (
                  <button
                    key={concern.id}
                    onClick={() => toggleOption('compliance_concerns', concern.id)}
                    className={`p-2 rounded-lg border text-xs text-left transition-all ${
                      formData.compliance_concerns.includes(concern.id) 
                        ? 'border-brand bg-brand text-white' 
                        : 'border-slate-200 hover:border-brand/50'
                    }`}
                  >
                    {concern.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(4)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={analyzeReadiness} 
                className="bg-brand hover:bg-brand-dark"
                disabled={isLoading}
              >
                {isLoading ? "Analyzing..." : "Check Readiness"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (result) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-primary">Digital Readiness Report</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Score Summary */}
        <div className="bg-brand/5 border border-brand/20 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Cpu className="h-5 w-5 text-brand" />
              <span className="font-semibold">Digital Maturity: {result.maturity_level}</span>
            </div>
            <div className={`text-3xl font-bold ${getScoreColor(result.scores.overall)}`}>
              {result.scores.overall}%
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Shield className="h-4 w-4 text-slate-500" />
                <span className="text-xs text-muted-foreground">Cybersecurity</span>
              </div>
              <p className={`text-xl font-bold ${getScoreColor(result.scores.cybersecurity)}`}>
                {result.scores.cybersecurity}%
              </p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Database className="h-4 w-4 text-slate-500" />
                <span className="text-xs text-muted-foreground">Data Mgmt</span>
              </div>
              <p className={`text-xl font-bold ${getScoreColor(result.scores.data_management)}`}>
                {result.scores.data_management}%
              </p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Cpu className="h-4 w-4 text-slate-500" />
                <span className="text-xs text-muted-foreground">AI Readiness</span>
              </div>
              <Badge className="bg-brand/10 text-brand capitalize">
                {result.scores.ai_readiness}
              </Badge>
            </div>
          </div>
        </div>

        {/* Risks */}
        {result.risks.length > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Identified Risks
            </h3>
            <div className="space-y-2">
              {result.risks.map((risk, index) => (
                <div key={index} className={`rounded-lg p-3 ${
                  risk.level === 'high' ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'
                }`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm">{risk.area}</span>
                    <Badge className={risk.level === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                      {risk.level}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600">{risk.description}</p>
                  <p className="text-xs text-slate-500 mt-1"><strong>Action:</strong> {risk.action}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations */}
        <div className="bg-slate-50 rounded-xl p-4 mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-brand" />
            Recommendations
          </h3>
          <div className="space-y-3">
            {result.recommendations.slice(0, 4).map((rec, index) => (
              <div key={index} className="bg-white rounded-lg p-3">
                <div className="flex items-start justify-between mb-1">
                  <span className="font-medium text-sm">{rec.area}</span>
                  <Badge className={rec.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}>
                    {rec.priority}
                  </Badge>
                </div>
                <p className="text-sm">{rec.action}</p>
                <p className="text-xs text-muted-foreground mt-1">💡 {rec.benefit}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
          <p className="text-xs text-amber-700 flex items-start gap-2">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            This assessment provides general guidance on digital readiness. A comprehensive risk assessment 
            should be conducted with qualified professionals for critical decisions.
          </p>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => { setResult(null); setStep(1); }} className="flex-1">
            Start Over
          </Button>
          <Button onClick={onClose} className="flex-1 bg-brand hover:bg-brand-dark">
            Request Consultation
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-primary">AI & Digital Readiness Checker</h2>
          <p className="text-sm text-muted-foreground">Step {step} of 5</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-slate-100 rounded-full h-2 mb-8">
        <div 
          className="bg-brand h-2 rounded-full transition-all duration-300"
          style={{ width: `${(step / 5) * 100}%` }}
        />
      </div>

      {renderStep()}
    </div>
  );
};

export default DigitalReadinessChecker;
